import { differenceInCalendarDays, differenceInDays, format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { CalendarItemsColor } from 'theme/tokens';

import { WorkCalendarRecord } from '../types';
import { DayInfoItem } from '../ui/Calendar/Day';

interface GetCalendarDayJobsParams {
    workCalendarRecord: WorkCalendarRecord;
    timeZone: string;
}

const shiftDateCache = new Map<string, { startTz: Date; endTz: Date }>();

export function getCalendarDayJobs({
    workCalendarRecord,
    timeZone
}: GetCalendarDayJobsParams): DayInfoItem[] {
    const jobs: DayInfoItem[] = [];
    const recordDateTz = toZonedTime(workCalendarRecord.date, timeZone);
    const recordDate = new Date(recordDateTz.getFullYear(), recordDateTz.getMonth(), recordDateTz.getDate());
    const recordDateMs = recordDate.getTime();

    for (const order of workCalendarRecord.orders) {
        if (order.shifts.length === 0) {
            continue;
        }

        for (const shift of order.shifts) {
            const cacheKey = `${ shift.dateTimeStart }-${ shift.dateTimeEnd }-${ timeZone }`;
            let shiftStartTz: Date;
            let shiftEndTz: Date;

            if (shiftDateCache.has(cacheKey)) {
                const cached = shiftDateCache.get(cacheKey)!;
                shiftStartTz = cached.startTz;
                shiftEndTz = cached.endTz;
            } else {
                shiftStartTz = toZonedTime(shift.dateTimeStart, timeZone);
                shiftEndTz = toZonedTime(shift.dateTimeEnd, timeZone);
                shiftDateCache.set(cacheKey, { startTz: shiftStartTz, endTz: shiftEndTz });
            }

            const shiftStartDate = new Date(shiftStartTz.getFullYear(), shiftStartTz.getMonth(), shiftStartTz.getDate());
            const shiftEndDate = new Date(shiftEndTz.getFullYear(), shiftEndTz.getMonth(), shiftEndTz.getDate());

            const isRelevantForThisDay =
                recordDateMs >= shiftStartDate.getTime() &&
                recordDateMs <= shiftEndDate.getTime();

            if (!isRelevantForThisDay) {
                continue;
            }

            const startTimeTz = format(shiftStartTz, 'HH:mm');
            const endTimeTz = format(shiftEndTz, 'HH:mm');
            const isFullDay = startTimeTz === '00:00' && endTimeTz === '23:59';

            const daysTotal = differenceInCalendarDays(shiftEndTz, shiftStartTz) + 1;
            const isOvernight = daysTotal === 2 && differenceInDays(shiftEndTz, shiftStartTz) === 0;
            const actualDaysTotal = isOvernight ? 1 : daysTotal;

            // Build display text respecting visibility settings
            const visibleParts: string[] = [];

            if (order.visibilitySetting.customer && order.customer) {
                visibleParts.push(order.customer);
            }

            if (order.visibilitySetting.department && order.department) {
                visibleParts.push(order.department);
            } else if (order.visibilitySetting.location && order.location) {
                visibleParts.push(order.location);
            }

            let displayText: string;

            if (actualDaysTotal === 1 && !isFullDay) {
                // Single day with time range
                if (visibleParts.length > 0) {
                    displayText = visibleParts.join(' • ');
                } else {
                    // Fallback to time range when no visible fields
                    displayText = `${ startTimeTz } - ${ endTimeTz }`;
                }
            } else {
                // Multi-day or full-day shift
                if (visibleParts.length > 0) {
                    displayText = visibleParts.join(' • ');
                } else {
                    // Fallback to time range for multi-day when no visible fields
                    displayText = `${ startTimeTz } - ${ endTimeTz }`;
                }
            }

            // Build tooltip respecting visibility settings
            const tooltipParts: string[] = [];

            if (order.visibilitySetting.department && order.department) {
                tooltipParts.push(order.department);
            } else if (order.visibilitySetting.location && order.location) {
                tooltipParts.push(order.location);
            }

            tooltipParts.push(
                actualDaysTotal > 1
                    ? `${ format(shiftStartTz, 'MMM d') } - ${ format(shiftEndTz, 'MMM d') }`
                    : format(shiftStartTz, 'MMM d')
            );

            jobs.push({
                text: displayText,
                color: CalendarItemsColor.job,
                startTimeTz,
                tooltip: order.restrictedAccess ? undefined : {
                    title: (order.visibilitySetting.customer && order.customer) ? order.customer : undefined,
                    text: tooltipParts.filter(Boolean).join(' • ') || undefined
                },
                href: order.restrictedAccess ? undefined : `/jobs/${ order.id }`
            });
        }
    }

    return jobs;
}
